/**
 * Página de Visualização de Item de Checklist - ConfereAI
 * Alinhada com a estrutura da tabela checklist_itens do PostgreSQL
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';

// Interface alinhada com a tabela checklist_itens do PostgreSQL
interface ChecklistItemDetail {
  id: number;
  checklist_id: number | null;
  categoria: string | null;
  descricao: string;
  tipo: string;
  opcoes: any | null;
  obrigatorio: boolean;
  ordem: number | null;
  valor: string | null;
  observacao: string | null;
  fotos: any | null;
  criado_em: string;
  atualizado_em: string;
  empresa_id: number;
}

const ChecklistItemView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<ChecklistItemDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadChecklistItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadChecklistItem = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.getChecklistItemById(Number(id)) as ApiResponse<any>;

      if (response.success && response.data) {
        setItem(response.data);
      } else {
        throw new Error(response.message || 'Erro ao carregar item');
      }
    } catch (error) {
      console.error('Erro ao carregar item:', error);
      setError('Erro ao carregar detalhes do item');
      utils.showNotification('Erro ao carregar detalhes do item', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTipo = (tipo: string): string => {
    const tipoMap: Record<string, string> = {
      'texto': 'Texto',
      'numero': 'Número',
      'selecao': 'Seleção',
      'multipla_escolha': 'Múltipla Escolha',
      'foto': 'Foto',
      'assinatura': 'Assinatura'
    };
    return tipoMap[tipo] || tipo;
  };

  const getTipoIcon = (tipo: string): string => {
    const iconMap: Record<string, string> = {
      'texto': 'fas fa-font',
      'numero': 'fas fa-hashtag',
      'selecao': 'fas fa-check-circle',
      'multipla_escolha': 'fas fa-check-double',
      'foto': 'fas fa-camera',
      'assinatura': 'fas fa-signature'
    };
    return iconMap[tipo] || 'fas fa-question-circle';
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/itens-checklist')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Item...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes do item...</p>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/itens-checklist')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Item não encontrado'}</h3>
              <p>O item solicitado não pôde ser carregado.</p>
              <button className="btn btn-primary" onClick={() => navigate('/itens-checklist')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/itens-checklist')}>
          <i className="fas fa-arrow-left"></i>
          Voltar
        </button>
        <h1>Detalhes do Item de Checklist</h1>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/itens-checklist/editar/${item.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Cabeçalho com informações principais */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <i className={getTipoIcon(item.tipo)} style={{ fontSize: '1.5rem', color: '#04C64F' }}></i>
              <h2>{item.descricao}</h2>
            </div>
            {item.obrigatorio && (
              <span className="status-badge status-danger">
                Obrigatório
              </span>
            )}
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label><i className="fas fa-list-alt" style={{ marginRight: '8px' }}></i>Tipo:</label>
                <span className="info-value">
                  <span className="status-badge status-info">{formatTipo(item.tipo)}</span>
                </span>
              </div>

              {item.categoria && (
                <div className="info-item">
                  <label><i className="fas fa-tag" style={{ marginRight: '8px' }}></i>Categoria:</label>
                  <span className="info-value">{item.categoria}</span>
                </div>
              )}

              {item.ordem !== null && (
                <div className="info-item">
                  <label><i className="fas fa-sort-numeric-up" style={{ marginRight: '8px' }}></i>Ordem:</label>
                  <span className="info-value">
                    <span className="status-badge status-secondary">{item.ordem}</span>
                  </span>
                </div>
              )}

              <div className="info-item">
                <label><i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>Obrigatório:</label>
                <span className="info-value">
                  <span className={`status-badge ${item.obrigatorio ? 'status-success' : 'status-warning'}`}>
                    {item.obrigatorio ? 'Sim' : 'Não'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Opções (se aplicável) */}
        {item.opcoes && Array.isArray(item.opcoes) && item.opcoes.length > 0 && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3><i className="fas fa-list-check" style={{ marginRight: '8px' }}></i>Opções Disponíveis</h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {item.opcoes.map((opcao, index) => (
                  <div key={index} style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <i className="fas fa-check" style={{ color: '#04C64F' }}></i>
                    <strong>{typeof opcao === 'string' ? opcao : opcao.valor || opcao}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Observações */}
        {item.observacao && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3><i className="fas fa-comment-dots" style={{ marginRight: '8px' }}></i>Observações</h3>
            </div>
            <div className="card-body">
              <p style={{ color: '#2c3e50', lineHeight: '1.8', fontSize: '1rem', whiteSpace: 'pre-wrap' }}>
                {item.observacao}
              </p>
            </div>
          </div>
        )}

        {/* Valor (se preenchido) */}
        {item.valor && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3><i className="fas fa-file-alt" style={{ marginRight: '8px' }}></i>Valor Registrado</h3>
            </div>
            <div className="card-body">
              <p style={{ color: '#2c3e50', fontSize: '1rem' }}>
                {item.valor}
              </p>
            </div>
          </div>
        )}

        {/* Fotos (se houver) */}
        {item.fotos && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3><i className="fas fa-images" style={{ marginRight: '8px' }}></i>Fotos Anexadas</h3>
            </div>
            <div className="card-body">
              <p style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                Este item possui fotos anexadas
              </p>
            </div>
          </div>
        )}

        {/* Informações de auditoria */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3><i className="fas fa-clock" style={{ marginRight: '8px' }}></i>Informações de Auditoria</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Criado em:</label>
                <span className="info-value">{formatDateTime(item.criado_em)}</span>
              </div>
              <div className="info-item">
                <label>Atualizado em:</label>
                <span className="info-value">{formatDateTime(item.atualizado_em)}</span>
              </div>
              <div className="info-item">
                <label>ID do Item:</label>
                <span className="info-value">#{item.id}</span>
              </div>
              {item.checklist_id && (
                <div className="info-item">
                  <label>ID do Checklist:</label>
                  <span className="info-value">#{item.checklist_id}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ações */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/itens-checklist')}
          >
            <i className="fas fa-list"></i>
            Voltar para Lista
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/itens-checklist/editar/${item.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar Item
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistItemView;
